// DocentesSection.js — Docentes del proyecto (foto + país + nombre + descripción)
// El admin elige cuáles mostrar (config.docentes_ids); vacío = todos.
//
// Tres disposiciones (`config.layout`):
//   'horizontal' (por defecto) — foto a la izquierda, texto al lado. Es el
//                                comportamiento de siempre.
//   'vertical'                 — foto arriba centrada, texto debajo.
//   'ficha'                    — tarjeta con la foto asomando por el borde
//                                superior, aro degradado y bandera en la esquina.
//   'tarjeta'                  — foto arriba de borde a borde, nombre y cargo
//                                debajo; al pulsar abre la ficha del docente
//                                (`popup`, activado por defecto en este modo).
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { BACKEND_BASE } from '../../../services/api';
import { buildBg } from '../bgHelpers';
import { urlBandera, normalizarPais } from '../../../utils/pais';
import { subrayadoCss } from '../tituloHelpers';

const Wrapper = styled.section`
  width: 100%;
  padding: ${p => p.$padding || '3.5rem 0'};
  background: ${p => p.$bg || '#1a0b2e'};
`;

const Inner = styled.div`
  max-width: ${p => p.$maxWidth || '760px'};
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const Title = styled.h2`
  font-size: ${p => p.$size || '30px'};
  color: ${p => p.$color || '#ffffff'};
  font-weight: 700;
  font-family: inherit;
  text-align: ${p => p.$align || 'left'};
  margin: 0 0 1.5rem;
  ${p => p.$subrayado || ''}
`;

const Intro = styled.div`
  margin: 0 0 1.6rem;
  font-size: 18px;
  line-height: 1.45;
  color: ${p => p.$color || '#808080'};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${p => p.$cols || 1}, 1fr);
  gap: ${p => p.$gap || '1rem'};
  /* La ficha asoma la foto por encima del borde de la tarjeta: hay que dejarle
     aire a la primera fila y ensanchar la separación entre filas para que la
     foto de una no pise la tarjeta de arriba. */
  ${p => p.$ficha && `
    padding-top: ${p.$asomo};
    row-gap: calc(${p.$gap || '1rem'} + ${p.$asomo});
    /* En una sola columna la separación de escritorio deja un hueco enorme:
       alcanza con el aire que necesita la foto que asoma. */
    @media (max-width: 768px) { row-gap: calc(1.6rem + ${p.$asomo}); }
  `}
  @media (max-width: 991px) {
    ${p => (p.$cols > 2 ? 'grid-template-columns: repeat(2, 1fr);' : '')}
  }
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  display: flex;
  flex-direction: ${p => (p.$modo === 'horizontal' ? 'row' : 'column')};
  align-items: ${p => (p.$modo === 'ficha' ? 'stretch' : 'center')};
  text-align: ${p => (p.$modo === 'vertical' ? 'center' : 'left')};
  gap: ${p => (p.$modo === 'horizontal' ? '1.1rem' : '0.6rem')};
  background: ${p => p.$bg || 'rgba(255,255,255,0.04)'};
  border: ${p => p.$grosor || '1px'} solid ${p => p.$border || 'rgba(255,255,255,0.06)'};
  border-radius: ${p => p.$radius || '16px'};
  padding: ${p => p.$padding || '1.1rem 1.4rem'};
  /* En la ficha, la foto se sale del borde superior: la tarjeta necesita ser
     contenedor posicionado y reservar ese espacio con el padding de arriba. */
  ${p => p.$modo === 'ficha' && `
    position: relative;
    padding-top: ${p.$padTop};
  `}
  /* Última fila incompleta centrada. Solo en escritorio: en los breakpoints la
     grilla cambia de columnas y forzar una columna concreta inventaría una
     columna fantasma. */
  ${p => p.$colInicio && `
    @media (min-width: 992px) { grid-column-start: ${p.$colInicio}; }
  `}
  @media (max-width: 480px) {
    ${p => (p.$modo === 'horizontal' ? 'flex-direction: column; text-align: center;' : '')}
  }
`;

const Foto = styled.div`
  width: ${p => p.$size || '84px'};
  height: ${p => p.$size || '84px'};
  flex-shrink: 0;
  border-radius: 50%;
  background-image: url(${p => p.$src});
  background-size: cover;
  background-position: center;
  background-color: ${p => p.$placeholder || 'rgba(255,255,255,0.1)'};
  display: flex; align-items: center; justify-content: center;
  font-size: 1.6rem; color: rgba(255,255,255,0.5);
`;

const Body = styled.div`flex: 1; min-width: 0; width: 100%;`;

/* Envuelve la foto. En la ficha se ancla al borde superior de la tarjeta y
   lleva el aro degradado (un borde pintado con el fondo del propio wrapper). */
const FotoWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  line-height: 0;
  ${p => p.$aro && `
    background: ${p.$aro};
    padding: ${p.$aroGrosor || '3px'};
    border-radius: 50%;
  `}
  ${p => p.$modo === 'ficha' && `
    position: absolute;
    top: -${p.$asomo};
    left: 50%;
    transform: translateX(-50%);
  `}
`;

/* Bandera: sobre la foto en el layout vertical, en la esquina de la tarjeta en
   la ficha (que es donde la pone el diseño). */
const BanderaImg = styled.img`
  position: absolute;
  width: ${p => p.$size || '30px'};
  height: ${p => p.$size || '30px'};
  border-radius: 50%;
  object-fit: cover;
  border: ${p => p.$borde ? `2px solid ${p.$borde}` : 'none'};
  background: #e2e8f0;
  ${p => p.$modo === 'ficha'
    ? `top: ${p.$top || '-16px'}; right: ${p.$right || '-14px'};`
    : p.$modo === 'vertical'
      ? 'right: 50%; transform: translateX(50%); bottom: -8px;'
      : 'right: -4px; bottom: 0;'}
`;

const Nombre = styled.h3`
  margin: 0 0 0.4rem;
  font-size: ${p => p.$size || '1.05rem'};
  font-weight: 700;
  color: ${p => p.$color || '#e11d8f'};
  line-height: 1.25;
`;

const Desc = styled.div`
  font-size: ${p => p.$size || '0.92rem'};
  line-height: 1.5;
  color: ${p => p.$color || 'rgba(255,255,255,0.78)'};
`;

const resolveImg = (img) => !img ? null : (img.startsWith('http') ? img : `${BACKEND_BASE}/${img}`);

// La descripción de un docente puede venir como texto plano o, si el proyecto
// es multi-idioma, como un JSON {es, pt, en}. Se resuelve con el `tl` de la
// landing para que Brasil vea el texto en portugués.
const textoDocente = (valor, tl) => {
  if (!valor) return '';
  const v = String(valor).trim();
  if (v.startsWith('{') && v.endsWith('}')) {
    try {
      const obj = JSON.parse(v);
      if (obj && typeof obj === 'object') return tl ? tl(obj) : (obj.es || '');
    } catch (_) { /* no era JSON: se usa tal cual */ }
  }
  return v;
};

// Bandera de un docente: primero la imagen que el admin haya cargado para ese
// país (`banderas_por_pais`, para usar los archivos de marca del proyecto) y si
// no hay, la de flagcdn deducida del nombre del país.
const banderaDe = (pais, mapa) => {
  if (!pais) return null;
  if (mapa && typeof mapa === 'object') {
    const clave = Object.keys(mapa).find(k => normalizarPais(k) === normalizarPais(pais));
    if (clave && mapa[clave]) return resolveImg(mapa[clave]);
  }
  return urlBandera(pais);
};

// ── Tarjeta con foto arriba y ficha emergente ────────────────────────────
const TarjetaDocente = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  text-align: center;
  background: ${p => p.$bg || '#f4f2f7'};
  border-radius: ${p => p.$radius || '8px'};
  overflow: hidden;
  font-family: inherit;
  cursor: ${p => (p.$clic ? 'pointer' : 'default')};
  transition: transform .25s ease, box-shadow .25s ease;
  &:hover { ${p => p.$clic && 'transform: translateY(-4px); box-shadow: 0 10px 24px rgba(37, 8, 68, 0.12);'} }
  &:focus-visible { outline: 2px solid ${p => p.$acento || '#63068e'}; outline-offset: 3px; }
  @media (prefers-reduced-motion: reduce) { transition: none; &:hover { transform: none; } }
`;

const TarjetaFoto = styled.div`
  height: ${p => p.$alto || '145px'};
  background: ${p => p.$fondo || '#22003d'} center/cover no-repeat;
  ${p => p.$src && `background-image: url(${p.$src});`}
`;

const TarjetaCuerpo = styled.div`
  padding: 14px 12px 10px;
  min-height: 82px;
  background: #fff;
  .nombre { margin: 0 0 2px; font-size: ${p => p.$sizeNombre || '15px'}; font-weight: 700; line-height: 1.2; color: ${p => p.$colorNombre || '#63068e'}; }
  .cargo { font-size: 14px; line-height: 1.2; color: ${p => p.$colorCargo || '#808080'}; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: rgba(22, 6, 38, .35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Ficha = styled.div`
  position: relative;
  width: 100%;
  max-width: 760px;
  max-height: calc(100vh - 40px);
  overflow: auto;
  background: #f6f4f8;
  border-radius: 10px;
  padding: 26px 26px 24px;
  box-shadow: 0 14px 40px rgba(28, 5, 52, .18);
  text-align: left;
  .cabecera { display: flex; align-items: center; gap: 16px; margin-bottom: 24px; }
  .avatar { width: 64px; height: 64px; border-radius: 16px; background: #e9e2f6 center/cover no-repeat; flex-shrink: 0; }
  .nombre { margin: 0 0 4px; padding: 0; font-size: 20px; font-weight: 700; line-height: 1.15; color: ${p => p.$acento}; }
  .cargo { font-size: 15px; color: #808080; font-weight: 500; }
  .bio, .bio p { font-size: 16px; line-height: 1.4; color: #796b8c; margin: 0; }
  @media (max-width: 600px) { padding: 22px 18px 18px; .cabecera { align-items: flex-start; } }
`;

const CerrarFicha = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 28px;
  height: 28px;
  border: 2px solid ${p => p.$acento};
  background: #fff;
  color: ${p => p.$acento};
  border-radius: 6px;
  font-size: 19px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
`;

const DocentesSection = ({ config, proyecto, tl }) => {
  const c = config;
  const [abierto, setAbierto] = useState(null);

  // La ficha se cierra con Escape y mientras está abierta la página no se mueve.
  useEffect(() => {
    if (!abierto) return undefined;
    const html = document.documentElement;
    const previo = html.style.overflow;
    html.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setAbierto(null); };
    document.addEventListener('keydown', onKey);
    return () => { html.style.overflow = previo; document.removeEventListener('keydown', onKey); };
  }, [abierto]);
  const todos = proyecto?.docentes || [];
  if (todos.length === 0) return null;

  // Selección: si hay ids, respeta ese orden; si no, todos.
  const ids = c.docentes_ids;
  let lista = todos;
  if (Array.isArray(ids) && ids.length) {
    lista = ids.map(id => todos.find(d => String(d.id) === String(id))).filter(Boolean);
  }
  if (lista.length === 0) return null;

  // Un mismo docente puede presentarse distinto según el curso: la sección
  // puede sobrescribir su descripción con `descripciones_docente = {id: texto}`.
  // Sin esa clave se usa la ficha del docente, como siempre.
  const overrides = c.descripciones_docente || {};
  if (Object.keys(overrides).length) {
    lista = lista.map(d => {
      const propio = overrides[d.id] != null ? overrides[d.id] : overrides[String(d.id)];
      return propio ? { ...d, descripcion: propio } : d;
    });
  }

  const modo = ['vertical', 'ficha', 'tarjeta'].includes(c.layout) ? c.layout : 'horizontal';
  const mostrarBandera = c.mostrar_bandera === true;
  const cols = parseInt(c.columnas) || 1;

  // Cuánto asoma la foto por encima de la tarjeta (la mitad de su alto) y el
  // aire que hay que dejar debajo para que no pise el nombre.
  const fotoSize = c.tamano_foto || (modo === 'ficha' ? '96px' : '84px');
  const asomo = c.asomo_foto || `calc(${fotoSize} / 2)`;
  const padTop = c.padding_superior_ficha || `calc(${fotoSize} / 2 + 1.2rem)`;

  // Con una última fila incompleta, el diseño centra lo que sobra en vez de
  // dejarlo pegado a la izquierda.
  const sobrantes = cols > 1 ? lista.length % cols : 0;
  const centrarUltima = c.centrar_ultima_fila === true && sobrantes > 0;
  const colInicioUltima = Math.floor((cols - sobrantes) / 2) + 1;

  const intro = c.descripcion ? tl(c.descripcion) : '';
  const acento = c.color_nombre || '#63068e';

  if (modo === 'tarjeta') {
    const conFicha = c.popup !== false;
    const docente = abierto != null ? lista.find(d => String(d.id) === String(abierto)) : null;
    return (
      <Wrapper $bg={buildBg(c, '#f8f5ff')} $padding={c.padding}>
        <Inner $maxWidth={c.ancho_maximo}>
          {c.titulo && (
            <Title
              $size={c.tamano_titulo}
              $color={c.color_titulo || '#1a0a2e'}
              $align={c.alineacion_titulo || 'left'}
              $subrayado={subrayadoCss(c)}
              dangerouslySetInnerHTML={{ __html: tl(c.titulo) }}
            />
          )}
          {intro && <Intro $color={c.color_descripcion || '#808080'} dangerouslySetInnerHTML={{ __html: intro }} />}
          <Grid $cols={cols} $gap={c.gap || '18px'}>
            {lista.map(d => (
              <TarjetaDocente
                key={d.id}
                type="button"
                as={conFicha ? 'button' : 'div'}
                $clic={conFicha && !!d.descripcion}
                $bg={c.color_tarjeta}
                $radius={c.radio_tarjeta}
                $acento={acento}
                onClick={conFicha && d.descripcion ? () => setAbierto(d.id) : undefined}
                aria-haspopup={conFicha && d.descripcion ? 'dialog' : undefined}
              >
                <TarjetaFoto $src={resolveImg(d.foto)} $alto={c.altura_foto} $fondo={c.color_fondo_foto} />
                <TarjetaCuerpo $sizeNombre={c.tamano_nombre} $colorNombre={acento} $colorCargo={c.color_descripcion}>
                  <h3 className="nombre">{d.nombre}</h3>
                  {d.cargo && <div className="cargo">{d.cargo}</div>}
                </TarjetaCuerpo>
              </TarjetaDocente>
            ))}
          </Grid>
        </Inner>
        {docente && (
          <Overlay onMouseDown={e => { if (e.target === e.currentTarget) setAbierto(null); }}>
            <Ficha role="dialog" aria-modal="true" aria-label={docente.nombre} $acento={acento}>
              <CerrarFicha type="button" aria-label="Cerrar" $acento={acento} onClick={() => setAbierto(null)}>×</CerrarFicha>
              <div className="cabecera">
                <div className="avatar" style={resolveImg(docente.foto) ? { backgroundImage: `url(${resolveImg(docente.foto)})` } : undefined} />
                <div>
                  <h3 className="nombre">{docente.nombre}</h3>
                  {docente.cargo && <div className="cargo">{docente.cargo}</div>}
                </div>
              </div>
              <div className="bio" dangerouslySetInnerHTML={{ __html: `<p>${textoDocente(docente.descripcion, tl).replace(/\n/g, '<br>')}</p>` }} />
            </Ficha>
          </Overlay>
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper $bg={buildBg(c, '#1a0b2e')} $padding={c.padding}>
      <Inner $maxWidth={c.ancho_maximo}>
        {c.titulo && (
          <Title
            $size={c.tamano_titulo}
            $color={c.color_titulo || '#ffffff'}
            $align={c.alineacion_titulo || 'left'}
            $subrayado={subrayadoCss(c)}
            dangerouslySetInnerHTML={{ __html: tl(c.titulo) }}
          />
        )}
        <Grid $cols={cols} $gap={c.gap} $ficha={modo === 'ficha'} $asomo={asomo}>
          {lista.map((d, i) => {
            const bandera = mostrarBandera ? banderaDe(d.pais, c.banderas_por_pais) : null;
            const esPrimeroUltimaFila = centrarUltima && i === lista.length - sobrantes;
            return (
              <Card
                key={d.id}
                $modo={modo}
                $bg={c.color_tarjeta}
                $border={c.color_borde_tarjeta}
                $grosor={c.grosor_borde_tarjeta}
                $radius={c.radio_tarjeta}
                $padding={c.padding_tarjeta}
                $asomo={asomo}
                $padTop={padTop}
                $colInicio={esPrimeroUltimaFila ? colInicioUltima : null}
              >
                <FotoWrap
                  $modo={modo}
                  $asomo={asomo}
                  $aro={c.aro_foto}
                  $aroGrosor={c.aro_grosor}
                >
                  <Foto
                    $src={resolveImg(d.foto)}
                    $size={fotoSize}
                    $placeholder={c.color_fondo_foto}
                  >
                    {!d.foto && '👤'}
                  </Foto>
                  {bandera && modo !== 'ficha' && (
                    <BanderaImg
                      src={bandera} alt={d.pais || ''} title={d.pais || ''}
                      $modo={modo} $size={c.tamano_bandera} $borde={c.color_borde_bandera}
                      loading="lazy"
                    />
                  )}
                </FotoWrap>
                {bandera && modo === 'ficha' && (
                  <BanderaImg
                    src={bandera} alt={d.pais || ''} title={d.pais || ''}
                    $modo={modo} $size={c.tamano_bandera} $borde={c.color_borde_bandera}
                    $top={c.bandera_top} $right={c.bandera_right}
                    loading="lazy"
                  />
                )}
                <Body>
                  <Nombre $color={c.color_nombre} $size={c.tamano_nombre}>{d.nombre}</Nombre>
                  {d.descripcion && (
                    <Desc
                      $color={c.color_descripcion}
                      $size={c.tamano_descripcion}
                      dangerouslySetInnerHTML={{ __html: textoDocente(d.descripcion, tl) }}
                    />
                  )}
                </Body>
              </Card>
            );
          })}
        </Grid>
      </Inner>
    </Wrapper>
  );
};

export default DocentesSection;
